import { useEffect, useMemo, useState } from 'react';
import { filter, isFinite, orderBy } from 'lodash';
import L from 'leaflet';

import { orderedStatuses } from '../../utilities/StatusUtilities';
import applyNoGapsTileLayer from './L.TileLayer.NoGaps';

applyNoGapsTileLayer(L);

const STATUS_INDEX = [...orderedStatuses].reverse().reduce((a, { color }, i) => ((a[color] = i), a), {});

export function useVisibleSites(map, siteQueryData) {
    const [pos, setPos] = useState(null);

    // we break this down into prinicpal components because the strict equality check doesn't always work with
    // the object returned from getBounds.
    const { neLat, neLng, swLat, swLng } = getNESWBounds(pos);

    // add event listeners for zoom/move since the map no longer re-renders when these change
    useEffect(() => {
        if (!map) return;

        setPos(map.getBounds());
        map.on('zoomend', () => setPos(map.getBounds()));
        map.on('moveend', () => setPos(map.getBounds()));
    }, [map]);

    return useMemo(() => {
        const bounds = toNESWBounds(neLat, neLng, swLat, swLng);

        return filter(siteQueryData, ({ site }) => isSiteWithin(site, bounds));
    }, [neLat, neLng, siteQueryData, swLat, swLng]);
}

function isSiteWithin(site, bounds) {
    const { latitude: lat, longitude: lng } = site;

    if (!isFinite(lat) || !isFinite(lng) || !bounds) {
        return false;
    }

    return bounds.contains(L.latLng(lat, lng));
}

function getNESWBounds(bounds) {
    if (!bounds) {
        return { neLat: 0, neLng: 0, swLat: 0, swLng: 0 };
    }

    const { lat: neLat, lng: neLng } = bounds.getNorthEast();
    const { lat: swLat, lng: swLng } = bounds.getSouthWest();

    return { neLat, neLng, swLat, swLng };
}

function toNESWBounds(neLat, neLng, swLat, swLng) {
    return L.latLngBounds([swLat, swLng], [neLat, neLng]);
}

export function sortSites(sites) {
    return orderBy(sites, [statusSorter, nameSorter], 'desc');
}

function statusSorter(meta) {
    const status = meta.status.worst;

    return STATUS_INDEX[status || 'gray'];
}

function nameSorter(meta) {
    return meta.site.name;
}

export function groupSiteStatus(sites) {
    const group = orderedStatuses.reduce((g, { color }) => ((g[color] = 0), g), {});

    return sites.reduce((g, site) => {
        g[site.status.worst || 'gray'] += 1;

        return g;
    }, group);
}

export function toLatLng(point) {
    const [x, y] = point;
    const result = L.Projection.SphericalMercator.unproject({ x, y });
    return [result.lat, result.lng];
}

export function distanceBetween(latLng1, latLng2) {
    // Algorithm from here:  https://www.movable-type.co.uk/scripts/latlong.html
    const [lat1, lng1] = latLng1;
    const [lat2, lng2] = latLng2;
    const R = 6371e3; // metres
    const φ1 = (lat1 * Math.PI) / 180; // φ, λ in radians
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // in metres
}
